"use client"

import React from 'react'

interface SearchHighlightProps {
  text: string
  searchTerm: string
  className?: string
  highlightClassName?: string
}

export function SearchHighlight({
  text,
  searchTerm,
  className = "",
  highlightClassName = "bg-yellow-300 text-gray-900 px-1.5 py-0.5 rounded font-semibold shadow-xs"
}: SearchHighlightProps) {
  if (!searchTerm || !text) {
    return <span className={className}>{text}</span>
  }

  // Escape special regex characters in search term
  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedSearchTerm})`, 'gi')
  const parts = text.split(regex)

  return (
    <span className={className}>
      {parts.map((part, index) => {
        const isMatch = regex.test(part)
        return isMatch ? (
          <mark 
            key={index} 
            className={highlightClassName}
            aria-label={`Search term: ${part}`}
          >
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        )
      })}
    </span>
  )
}

// Alternative component for React elements (JSX)
interface SearchHighlightJSXProps {
  children: React.ReactNode
  searchTerm: string
  className?: string
  highlightClassName?: string
}

export function SearchHighlightJSX({
  children,
  searchTerm,
  className = "",
  highlightClassName = "bg-yellow-300 text-gray-900 px-1.5 py-0.5 rounded font-semibold shadow-xs"
}: SearchHighlightJSXProps) {
  if (!searchTerm || !children) {
    return <span className={className}>{children}</span>
  }

  // Convert children to string for processing
  const textContent = React.Children.toArray(children)
    .map(child => {
      if (typeof child === 'string') return child
      if (React.isValidElement(child) && typeof (child.props as any)?.children === 'string') {
        return (child.props as any).children
      }
      return ''
    })
    .join('')

  return (
    <SearchHighlight
      text={textContent}
      searchTerm={searchTerm}
      className={className}
      highlightClassName={highlightClassName}
    />
  )
} 
